import { useEffect, useRef, useState } from "react";
import { ZoomOut } from "lucide-react";

/**
 * PinchZoomFrame — wrapper de visualização (mobile-only).
 *
 * Aplica APENAS uma transformação CSS (scale + translate) sobre os filhos.
 * Não altera coordenadas clínicas, não toca em landmarks, scoring nem
 * pipeline MediaPipe. Pensado para permitir ao osteopata aproximar
 * visualmente uma vista numa avaliação sem afetar quaisquer cálculos.
 *
 *  - Pinch (2 dedos): zoom (1× → maxScale)
 *  - Drag (1 dedo) com zoom > 1: pan
 *  - Toque num landmark: a interação 1-dedo respeita o drag do landmark
 *  - Botão flutuante "↺ N×" para repor (apenas visível enquanto zoomed)
 *  - Em desktop / dispositivos sem touch: passthrough total (zero overhead)
 */
export default function PinchZoomFrame({ children, enabled = true, maxScale = 4, className = "" }) {
  const ref = useRef(null);
  const [t, setT] = useState({ s: 1, x: 0, y: 0 });
  const transformRef = useRef(t);
  transformRef.current = t;
  const gestureRef = useRef({
    pinching: false,
    panning: false,
    startDist: 0,
    startScale: 1,
    startMid: { x: 0, y: 0 },
    startTx: 0,
    startTy: 0,
    lastTouch: null,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const isTouch = typeof window !== "undefined" &&
      ("ontouchstart" in window || (navigator?.maxTouchPoints ?? 0) > 0);
    if (!isTouch) return;

    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const mid  = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

    const onTouchStart = (e) => {
      const g = gestureRef.current;
      const transform = transformRef.current;
      if (e.touches.length === 2) {
        e.preventDefault();
        g.pinching = true;
        g.panning = false;
        g.startDist = dist(e.touches[0], e.touches[1]);
        g.startMid = mid(e.touches[0], e.touches[1]);
        g.startScale = transform.s;
        g.startTx = transform.x;
        g.startTy = transform.y;
      } else if (e.touches.length === 1 && transform.s > 1.05) {
        // Não roubar o gesto se o utilizador iniciou num landmark draggable
        const target = e.target;
        const onLandmark = target?.closest?.('button[data-testid^="landmark-"]');
        if (onLandmark) return;
        e.preventDefault();
        g.panning = true;
        g.pinching = false;
        g.lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        g.startTx = transform.x;
        g.startTy = transform.y;
      }
    };

    const clampPan = (s, x, y) => {
      // Limita o pan para que o conteúdo zoomed não saia totalmente da viewport
      const r = el.getBoundingClientRect();
      const maxX = ((s - 1) * r.width) / 2;
      const maxY = ((s - 1) * r.height) / 2;
      return {
        x: Math.max(-maxX, Math.min(maxX, x)),
        y: Math.max(-maxY, Math.min(maxY, y)),
      };
    };

    const onTouchMove = (e) => {
      const g = gestureRef.current;
      if (g.pinching && e.touches.length === 2) {
        e.preventDefault();
        const d = dist(e.touches[0], e.touches[1]);
        const ratio = d / Math.max(1, g.startDist);
        const newS = Math.max(1, Math.min(maxScale, g.startScale * ratio));
        // Âncora o zoom no ponto médio do gesto
        const r = el.getBoundingClientRect();
        const ox = g.startMid.x - r.left - r.width / 2;
        const oy = g.startMid.y - r.top - r.height / 2;
        const k = newS / g.startScale;
        let nx = (g.startTx - ox) * k + ox;
        let ny = (g.startTy - oy) * k + oy;
        if (newS <= 1.001) { nx = 0; ny = 0; }
        const c = clampPan(newS, nx, ny);
        const next = { s: newS, x: c.x, y: c.y };
        transformRef.current = next;
        setT(next);
      } else if (g.panning && e.touches.length === 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - g.lastTouch.x;
        const dy = e.touches[0].clientY - g.lastTouch.y;
        const transform = transformRef.current;
        const c = clampPan(transform.s, g.startTx + dx, g.startTy + dy);
        const next = { ...transform, x: c.x, y: c.y };
        transformRef.current = next;
        setT(next);
      }
    };

    const onTouchEnd = (e) => {
      const g = gestureRef.current;
      if (e.touches.length < 2) g.pinching = false;
      if (e.touches.length === 1 && transformRef.current.s > 1.05) {
        const remainingTouch = e.touches[0];
        const onLandmark = remainingTouch.target?.closest?.('button[data-testid^="landmark-"]');
        if (!onLandmark) {
          g.panning = true;
          g.lastTouch = { x: remainingTouch.clientX, y: remainingTouch.clientY };
          g.startTx = transformRef.current.x;
          g.startTy = transformRef.current.y;
        }
      } else if (e.touches.length === 0) {
        g.panning = false;
      }
    };
    const onTouchCancel = () => {
      const g = gestureRef.current;
      g.pinching = false;
      g.panning = false;
      g.lastTouch = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd,   { passive: false });
    el.addEventListener("touchcancel", onTouchCancel, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
      el.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [enabled, maxScale]);

  // Auto-reset quando o componente é desmontado/remontado: garantido pelo useState inicial.

  const reset = () => {
    const next = { s: 1, x: 0, y: 0 };
    transformRef.current = next;
    gestureRef.current.panning = false;
    gestureRef.current.pinching = false;
    setT(next);
  };
  const zoomed = t.s > 1.02;
  const animating = !(gestureRef.current.pinching || gestureRef.current.panning);

  return (
    <div
      ref={ref}
      className={`relative ${className}`}
      style={{ touchAction: zoomed ? "none" : "pan-y" }}
      data-testid="pinch-zoom-frame"
    >
      <div
        style={{
          transform: `translate(${t.x}px, ${t.y}px) scale(${t.s})`,
          transformOrigin: "center center",
          transition: animating ? "transform 180ms ease-out" : "none",
          willChange: "transform",
        }}
      >
        {children}
      </div>

      {zoomed && (
        <button
          type="button"
          data-testid="pinch-zoom-reset"
          onClick={reset}
          aria-label="Repor zoom"
          className="absolute top-2 right-2 z-30 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-slate-950/85 border border-sky-500/40 text-sky-200 text-[11px] font-mono backdrop-blur shadow-lg shadow-sky-500/10"
          style={{ touchAction: "manipulation" }}
        >
          <ZoomOut className="w-3.5 h-3.5" /> {t.s.toFixed(1)}×
        </button>
      )}
    </div>
  );
}
